/**
 * What a reading actually saw, and a way to keep the part of it nothing caught.
 *
 * THE GAP THIS FILLS. The harvester reads a page, hands the text to a model, and
 * keeps only what maps onto an existing resource's columns. Everything else is
 * discarded - the paragraph on how a cartridge depth is chosen, the aftercare
 * wording, the parking note - and from the review list that is indistinguishable
 * from the page not mentioning it. A clinic could see facts on its own website
 * that articles were never going to use, with nowhere to put them and no way to
 * tell whether the reader had even seen them.
 *
 * So the page text is stored with the job (migration 081) and shown here. Select
 * a passage, give it a name, and it becomes a 強み row of kind その他: a labelled
 * fact that goes through the same DRAFT → 承認 gate as everything else and is
 * cited by that name when an article is written.
 *
 * WHY SELECTION AND NOT A SECOND MODEL PASS. Asking the model again is asking
 * the thing that already declined to keep it. The person looking at the page
 * knows which paragraph matters and what to call it, and that judgement is the
 * entire value of it - a machine-generated label would be the same discard
 * problem with more steps.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError, get, post } from '../api.ts';
import { Modal } from './Modal.tsx';

interface PageTextResponse {
  url: string;
  text: string;
}

export function PageText({
  sourceId,
  storeId,
  clinicId,
  onSaved,
}: {
  sourceId: string;
  /** Which store the material belongs to. '' or undefined is clinic-wide. */
  storeId?: string | undefined;
  /**
   * Set on the JMC screens, absent on the clinic's own.
   *
   * The two mounts differ only in scoping: a clinic's people are inside their
   * clinic already, and a JMC operator names which clinic they are working on.
   * Passing the id switches both reads to the platform mount rather than
   * duplicating this panel.
   */
  clinicId?: string | undefined;
  /** Something was registered, so the list behind can re-read. */
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<PageTextResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    void get<PageTextResponse>(
      clinicId
        ? `/admin/clinics/${clinicId}/sources/${sourceId}/text`
        : `/sources/${sourceId}/text`,
    )
      .then(setPage)
      .catch(() => setError('このページの本文を読み込めませんでした'));
  }, [open, sourceId, clinicId]);

  /*
   * The selection, taken from the browser rather than tracked keystroke by
   * keystroke. Read on mouseup and on keyup so a keyboard selection works too;
   * anything outside this panel is ignored, or clicking the label field would
   * clear what was chosen.
   */
  function readSelection() {
    const selection = window.getSelection();
    const text = selection?.toString() ?? '';
    if (text.trim() === '') return;
    if (bodyRef.current && selection?.anchorNode && !bodyRef.current.contains(selection.anchorNode)) {
      return;
    }
    setSelected(text.trim());
  }

  async function save() {
    if (selected.trim() === '' || label.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      await post(clinicId ? `/admin/clinics/${clinicId}/facts/strengths` : '/strengths', {
        title: label.trim(),
        // 4000 is the column's ceiling; a longer selection is cut rather than
        // refused, because the writer chose the passage and a 400 here would
        // lose it.
        body: selected.trim().slice(0, 4000),
        // Material none of the four named categories describes. See migration
        // 084 for why this is a category rather than a table of its own.
        category: 'OTHER',
        ...(storeId ? { storeId } : {}),
      });
      setSaved((count) => count + 1);
      setLabel('');
      setSelected('');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存できませんでした');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="linkbutton" type="button" onClick={() => setOpen(true)}>
        読み取った本文を見る
      </button>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setSelected('');
          setLabel('');
        }}
        title="読み取ったページの本文"
        caption="残しておきたい部分を選び、見出しを付けて登録します。登録したものは下書きです。"
        wide
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        {page === null && !error ? (
          <p className="muted small">読み込み中…</p>
        ) : page && page.text === '' ? (
          <p className="muted small">
            このページの本文は保存されていません。もう一度読み取ると保存されます。
          </p>
        ) : (
          <>
            <p className="muted small" style={{ marginTop: 0 }}>
              {page?.url}
            </p>
            <div
              ref={bodyRef}
              className="pagetext"
              onMouseUp={readSelection}
              onKeyUp={readSelection}
            >
              {page?.text}
            </div>

            {/*
              The chosen passage, echoed. Not decoration: the browser's own
              highlight is lost the moment focus moves into the field below, and
              without this the writer is naming something they can no longer see.
            */}
            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor="note-label">
                見出し
                <span className="req">必須</span>
              </label>
              <input
                id="note-label"
                maxLength={120}
                placeholder="例：施術後のスキンケア"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
              <span className="field__hint">
                この名前で記事に引用されます。選んだ本文は「強み・方針・設備」に、種別
                「その他」の下書きとして入ります。
              </span>
            </div>

            <div className="pagetext__picked">
              {selected === '' ? (
                <span className="muted small">上の本文から、残したい部分を選んでください。</span>
              ) : (
                <>
                  <span className="muted small">
                    選択中 {selected.length.toLocaleString('ja-JP')}文字
                  </span>
                  <p style={{ margin: '4px 0 0' }}>{selected.slice(0, 400)}</p>
                </>
              )}
            </div>

            <div className="editpanel__actions">
              <button
                className="btn btn--sm"
                type="button"
                disabled={busy || selected.trim() === '' || label.trim() === ''}
                onClick={() => void save()}
              >
                {busy ? '登録中…' : 'この部分を登録する'}
              </button>
              {saved > 0 && (
                <span className="muted small" role="status">
                  {saved}件を「その他」として登録しました。続けて選べます。
                </span>
              )}
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
